import { useEffect, useState } from "react";
import api from "../utils/api";
import { HiOutlineEye } from "react-icons/hi";
import Modal from "../components/Modal";
import ViewContract from "../components/contract/ViewContract";
import EditContract from "../components/contract/EditContract";
import AddContract from "../components/contract/AddContract";
import AddBtn from "../components/AddBtn";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";
import RoleAccess from "../components/RoleAccess";
import { useSearchParams } from "react-router-dom";

export default function AllContracts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromUrl = searchParams.get("search");
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState({ text: queryFromUrl, date: "" });
  const [textSearchInput, setTextSearchInput] = useState("");
  const [dateSearchInput, setDateSearchInput] = useState("");

  const [selectedContract, setSelectedContract] = useState(null);
  const [isModalOpenForAdd, setIsModalOpenForAdd] = useState(false);
  const [isModalOpenForView, setIsModalOpenForView] = useState(false);
  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);

  const itemsPerPage = 5;

  const fetchContracts = async (page = 1, text = "", date = "") => {
    setLoading(true);
    try {
      const res = await api.get("/contracts/user/list", {
        params: {
          page,
          limit: itemsPerPage,
          text,
          date,
        },
        withCredentials: true,
      });

      setContracts(res.data.contracts || []);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
      }
    } catch (err) {
      console.error("Error fetching contracts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const activeQuery = searchParams.get("search");
    setTextSearchInput(activeQuery);
    setSearch((prev) => ({ ...prev, text: activeQuery }));
    setCurrentPage(1);
  }, [searchParams]);

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    fetchContracts(currentPage, search.text, search.date);
  }, [currentPage, search]);

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch({ text: textSearchInput, date: dateSearchInput });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClear = () => {
    setTextSearchInput("");
    setDateSearchInput("");
    setCurrentPage(1);
    setSearch({ text: "", date: "" });
    setSearchParams({});
  };

  const deleteContract = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contract?"))
      return;

    try {
      await api.delete(`/contracts/${id}`, { withCredentials: true });

      fetchContracts(currentPage, search.text, search.date);
    } catch (err) {
      console.error(err);
      alert("Failed to delete contract!");
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpenForAdd}
        onClose={() => setIsModalOpenForAdd(false)}
      >
        <AddContract
          contentOwnerId={null}
          productId={null}
          onSuccess={() => {
            setIsModalOpenForAdd(false);
            setCurrentPage(1);
            fetchContracts(1, search.text, search.date);
          }}
        />
      </Modal>

      <Modal
        isOpen={isModalOpenForView}
        onClose={() => setIsModalOpenForView(false)}
      >
        <ViewContract contract={selectedContract} />
      </Modal>

      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {selectedContract && (
          <EditContract
            contract={selectedContract}
            onSuccess={() => {
              setIsModalOpenForEdit(false);
              fetchContracts(currentPage, search.text, search.date);
            }}
          />
        )}
      </Modal>

      <div>
        <h1 className="text-2xl font-bold mt-10 ml-2 mb-3">All Contracts</h1>

        <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
          <RoleAccess allow={["Admin", "Manager"]} role={userRole}>
            <div className="mb-4">
              <AddBtn
                onClick={() => setIsModalOpenForAdd(true)}
                label="Add Contract"
              />
            </div>
          </RoleAccess>
          <div className="mb-4 flex justify-end gap-2">
            <input
              type="text"
              placeholder="Search by contract ID, contract name, or artwork..."
              value={textSearchInput}
              onChange={(e) => setTextSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-[450px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={dateSearchInput}
              onChange={(e) => setDateSearchInput(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
            >
              Search
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition"
            >
              Clear
            </button>
          </div>

          <table className="w-full text-sm border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl">
                  Contract ID
                </th>
                <th className="px-4 py-3 text-center">Contract Name</th>
                <th className="px-4 py-3 text-center">Signing Date</th>
                <th className="px-4 py-3 text-center">Expiry Date</th>
                <th className="px-4 py-3 text-center">Artworks</th>
                <th className="px-4 py-3 text-right rounded-tr-xl rounded-br-xl">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    Loading contracts...
                  </td>
                </tr>
              ) : contracts.length ? (
                contracts.map((c) => (
                  <tr
                    key={c._id}
                    className="border-t transition hover:bg-slate-100"
                  >
                    <td className="px-4 py-4 font-medium text-blue-600">
                      {c.contractId}
                    </td>
                    <td className="px-4 py-4 text-center">{c.contractName}</td>
                    <td className="px-4 py-4 text-center">
                      {new Date(c.signingDate).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {new Date(c.expiryDate).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {c.artworks?.length || 0}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-4">
                        <button
                          onClick={() => {
                            setSelectedContract(c);
                            setIsModalOpenForView(true);
                          }}
                        >
                          <HiOutlineEye className="h-5 w-5 text-green-400 hover:text-green-700 transition transform hover:scale-110" />
                        </button>
                        <RoleAccess
                          allow={["Admin", "Manager"]}
                          role={userRole}
                        >
                          <EditBtn
                            onClick={() => {
                              setSelectedContract(c);
                              setIsModalOpenForEdit(true);
                            }}
                            label="Edit Contract"
                          />
                          <DeleteBtn
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteContract(c._id);
                            }}
                            label="Delete Contract"
                          />
                        </RoleAccess>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No contracts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 mt-4">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <span className="px-3 py-1 font-medium">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
